import { useModal } from "@homebound/beam";
import { AiEntityProposalType, AiFieldProposalStatus } from "src/generated/graphqlTypes";
import { ConfirmationModal } from "src/routes/components/ConfirmationModal";
import { disableBasedOnPotentialOperation } from "src/routes/components/PotentialOperationsUtils";
import { PlanPackageAiReviewBanner } from "src/routes/libraries/plan-package-v2/components/PlanPackageAiReviewBanner";
import {
  ReviewDecision,
  useResolveTakeoffProposals,
  useTakeoffProposals,
} from "src/routes/libraries/plan-package-v2/components/takeoffs/useTakeoffProposals";
import { useTakeoffsStore } from "src/routes/libraries/plan-package/takeoffs/TakeoffsManagerContext";

/**
 * Accept All / Ignore All for the plan's pending AI takeoff lines.
 *
 * Proposals come from the plan package rather than the table's rows, since the table only loads a page of lines.
 */
export function TakeoffsAiReviewBanner() {
  const canEditLineItems = useTakeoffsStore((state) => state.planPackage.canEditLineItems);
  const { proposalsByTliId } = useTakeoffProposals();
  const resolve = useResolveTakeoffProposals();
  const { openModal } = useModal();

  const proposals = [...proposalsByTliId.values()];
  // There's no first-import marker, so all-CREATE proposals get the first-import copy
  const hasOnlyCreates = proposals.every((aep) => aep.type.code === AiEntityProposalType.Create);

  async function review(decision: ReviewDecision) {
    try {
      await resolve(proposals, decision);
    } catch {
      // Our useMutation wrapper has already shown the error snackbar. The save still rejects so callers like the delete
      // modal skip their success snackbar, but nothing runs after this one, so don't let it escape the button unhandled.
    }
  }

  function reconcileAll(decision: ReviewDecision) {
    if (decision === AiFieldProposalStatus.Accepted) return review(decision);
    openModal({
      content: (
        <ConfirmationModal
          title="Ignore All AI-Generated Changes?"
          label="Ignore All AI Lines"
          confirmationMessage="'Ignoring all' will remove all AI-generated changes from this takeoff. You won't be able to restore these changes after they're ignored."
          onConfirmAction={() => review(decision)}
        />
      ),
    });
  }

  return (
    <PlanPackageAiReviewBanner
      hasPending={proposals.nonEmpty}
      onReconcileAll={reconcileAll}
      disabled={disableBasedOnPotentialOperation(canEditLineItems)}
      {...(hasOnlyCreates
        ? {
            title: "AI-generated takeoff scope is ready for review.",
            message: "Review and refine the generated lines below, and accept them when you're ready.",
          }
        : {
            title: "Review changes to your takeoffs found in your latest document capture.",
            message: "Review the changes highlighted below.",
          })}
    />
  );
}
